import React, { useEffect, useRef, useState } from 'react';
import '../styles/RunComps.css';
import '../styles/TestView.css';
import { Header, AllCompsMap, ImageGallery, ScrollableImages, PropertyCard, SelectedComps, PropertyCardDetail, MapLegend, Switch, SelectionChip, SearchInput, PredictiveSearch, CompFilters, ExitScreen, Button, ReportLoadingAnimation, CompsOverlay } from '../components';
import { checkOnAuthStateChanged, propertyGatewayString, cloudFunctionV2, sendErrorEmail, getDocument, getSubDocument, setSelectedPropertyTransaction, regenerateTimeline, setSelectedPropertyReportTransaction, setSubData, recordEvent, filterByCondition, filterByDistance, filterByRecency, filterByType, filterBySimilarity, propertyGatewayStringReRun } from '../functions';
import { useNavigate, useParams } from 'react-router-dom';
import { useJsApiLoader } from '@react-google-maps/api';
import { House, Location, MagnifyingGlass, MapBubble, YearBuilt } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';
import { Settings01Icon } from 'hugeicons-react';

const libraries = process.env.REACT_APP_GOOGLE_LIBRARIES.split(',');

function TestView() {
    const [loggedIn, setLoggedIn] = useState(null);
    const [property, setProperty] = useState(null);
    const [userId, setUserId] = useState(null);
    const [allComps, setAllComps] = useState([]);
    const [filteredComps, setFilteredComps] = useState([]);
    const [bestComps, setBestComps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [companyId, setCompanyId] = useState(null);
    const [clickedZPID, setClickedZPID] = useState(null);
    const [viewImageGallery, setViewImageGallery] = useState(false);
    const [viewScrollableImages, setViewScrollableImages] = useState(false);
    const [imageViewProperty, setImageViewProperty] = useState(null);
    const [imageViewPropertyIndex, setImageViewPropertyIndex] = useState(0);
    const [selectedComps, setSelectedComps] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [detailedView, setDetailedView] = useState(null);
    const [qualifyingDescriptions, setQualifyingDescriptions] = useState([]);
    const [hoverZPID, setHoverZPID] = useState(null);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [neighbourhoodData, setNeighbourhoodData] = useState([]);
    const [tracts, setTracts] = useState([]);
    const [neighbourhoodBounds, setNeighbourhoodBounds] = useState([]);
    const [hoverMapLegend, setHoverMapLegend] = useState(null);
    const [showHeatmap, setShowHeatmap] = useState(false);
    const [selectedConditions, setSelectedConditions] = useState(["remodelled", "well-maintained"]);
    const [selectedPanel, setSelectedPanel] = useState("all");
    const [compTypeDropdownOpen, setCompTypeDropdownOpen] = useState(null);
    const [linkCopied, setLinkCopied] = useState(false);
    const [linkCopiedError, setLinkCopiedError] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [address, setAddress] = useState("");
    const [suggestedComps, setSuggestedComps] = useState([]);
    const [selectedType, setSelectedType] = useState(["sold", "pending", "for-sale"]);
    const [typeDropdown, setTypeDropdown] = useState(false);
    const [distance, setDistance] = useState(10);
    const [recencyMonths, setRecencyMonths] = useState(12);
    const [bedsDifference, setBedsDifference] = useState(null);
    const [bathsDifference, setBathsDifference] = useState(null);
    const [sqftDifference, setSqftDifference] = useState(null);
    const [yearBuiltDifference, setYearBuiltDifference] = useState(null);
    const [histogramDropdownOpen, setHistogramDropdownOpen] = useState(false);
    const [loadingReRun, setLoadingReRun] = useState(false);
    const [innerWidth, setInnerWidth] = useState(window.innerWidth);
    const [compsOverlayExpanded, setCompsOverlayExpanded] = useState(false);
    const [mapType, setMapType] = useState("roadmap");
    const [rawMap, setRawMap] = useState(null);
    const [subjectPropertyHover, setSubjectPropertyHover] = useState(false);
    const [conditionUpdate, setConditionUpdate] = useState([]);
    const params = useParams();
    const navigate = useNavigate();
    const propertyId = params === null ? null : params.id;
    const countyId = params === null ? null : params.countyId;
    const clickedComp = clickedZPID === null ? null : allComps[allComps.findIndex(e => e.uniqueIds.zillowId === clickedZPID)];
    const clickedCompDescriptionIndex = qualifyingDescriptions.findIndex(e => e.zpid === clickedZPID);
    const clickedCompDescription = clickedCompDescriptionIndex === -1 ? null : qualifyingDescriptions[clickedCompDescriptionIndex].description;
    const compsRef = useRef(null);
    const mobileSelectedCompsBody = useRef(null);
    const fullSelectedComps = allComps.filter(e => selectedComps.findIndex(x => x.zpid === e.uniqueIds.zillowId) !== -1);
    const pathname = window.location.pathname;
    const demoVersion = pathname.includes("/demos/") ? true : false;
    const readOnly = false;
    const bodyRef = useRef(null);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: JSON.parse(process.env.REACT_APP_GOOGLE).key,
        libraries: libraries
    });

    const conditionOptions = [
        {
            label: "All",
            value: "all",
            selected: selectedConditions.indexOf("all") !== -1 ? true : false
        },
        {
            label: "Recently Remodeled",
            value: "remodelled",
            selected: selectedConditions.indexOf("remodelled") !== -1 ? true : false
        },
        {
            label: "Well-maintained",
            value: "well-maintained",
            selected: selectedConditions.indexOf("well-maintained") !== -1 ? true : false
        },
        {
            label: "Moderate rehab",
            value: "moderate-rehab",
            selected: selectedConditions.indexOf("moderate-rehab") !== -1 ? true : false
        },
        {
            label: "Gut rehab",
            value: "gut-rehab",
            selected: selectedConditions.indexOf("gut-rehab") !== -1 ? true : false
        },
        {
            label: "Unknown condition",
            value: "unknown",
            selected: selectedConditions.indexOf("unknown") !== -1 ? true : false
        }
    ];

    useEffect(() => {

        const fetchUserData = async() => {
            const user = await checkOnAuthStateChanged();
            if ( user.status === 200 && demoVersion === false ) {
                setLoggedIn(true);
                setUserId(user.userId);

                const colRef = "users";
                const docRef = user.userId;
                const queryData = await getDocument(colRef, docRef);
                if ( queryData.status === 200 ) {
                    const data = queryData.data.data;
                    setCompanyId(data.companyId);
                    fetchPropertyData(user.userId, data.companyId);
                }
            }
            else if ( demoVersion === true ) {
                const colRef = "demo_properties";
                const docRef = "list";
                const queryData = await getDocument(colRef, docRef);
                if ( queryData.status === 200 ) {
                    const data = queryData.data.data;
                    const propertyIndex = data.indexOf(propertyId);
                    if ( propertyIndex === -1 ) {
                        navigate("/");
                        setLoggedIn(false);
                    }
                    else {
                        fetchPropertyData(null, null);
                    }
                }
                else {
                    navigate("/");
                    setLoggedIn(false);
                }
            }
            else {
                navigate("/");
                setLoggedIn(false);
            }
        };

        const getNeighbourhoodData = async() => {
			const neighbourhoodPath = `geo_output/map_neighborhood/county/${countyId}.json`;
			const params = {
				s3Data: {
					key: neighbourhoodPath,
					bucketName: "residentialpropertydata"
				},
				type: "s3"
			}
			const awsURL = process.env.REACT_APP_AWS_QUERY_URL;
			const queryData = await cloudFunctionV2(awsURL, params);
			if ( queryData.status === 200 ) {
				const body = queryData.body;
				setNeighbourhoodData(body);

				const newBounds = [];
				const getTracts = [];
				for (let index = 0; index < body.length; index++) {
					const element = body[index];
					getTracts.push(element.region.tractId);
					const tractPolygon = element.tractPolygon.flat(1);
					newBounds.push(tractPolygon);
				};
				setTracts(getTracts);
				setNeighbourhoodBounds(newBounds);
			}
		}

        const fetchPropertyData = async(savedUserId, savedCompanyId) => {
            const gatewayURL = process.env.REACT_APP_AWS_QUERY_URL;
            const resourceId = "propertyDetailV2";
            const propertyParams = {
                propertyId: propertyId,
                userId: savedUserId,
                countyId: countyId
            }
            const getGatewayQueryString = await propertyGatewayString("propertyDetailv2?", propertyParams);
            const getGatewayParams = {
                type: "gateway",
                resourceId: resourceId,
                queryString: getGatewayQueryString
            };
            const getGateway = await cloudFunctionV2(gatewayURL, getGatewayParams);
            if ( getGateway.status === 200 ) {
                const body = getGateway.body;
                if ( body.error !== undefined ) {
                    alert(`Error: ${body.error}`);
                    return;
                }
                const data = body.data;
                document.title = `${data.propertyDetails.addressDetails.streetAddress}, ${data.propertyDetails.addressDetails.city}, ${data.propertyDetails.addressDetails.state} ${data.propertyDetails.addressDetails.postalCode} | Clozers`;
                
                setProperty(data.propertyDetails);
                if ( savedCompanyId !== null ) {
                    querySavedProperty(savedCompanyId, data.propertyDetails);
                }
                const salesComparables = data.salesComparables.comps;
                const recentlySold = salesComparables.recentlySold;
                const forSale = salesComparables.forSale;
                const fullList = recentlySold.concat(forSale);

                const amendedFullList = [];
                for (let index = 0; index < fullList.length; index++) {
                    const element = fullList[index].propertyDetails;
                    // const liamOverallScore =  await getWeightedScore([element.compDetails.locationScore, element.compDetails.recencyScore, element.compDetails.similarityScore]);
                    // element.compDetails.overallScore = liamOverallScore;
                    amendedFullList.push(element);
                }
                amendedFullList.sort((a, b) => b.compDetails.overallScore - a.compDetails.overallScore);
                setAllComps(amendedFullList);
                getNeighbourhoodData();

                await regenerateTimeline(amendedFullList);

                const newFilteredComps = [];
                for (let index = 0; index < amendedFullList.length; index++) {
                    const element = amendedFullList[index];
                    const propertyCondition = element.compDetails.propertyCondition;
                    if ( 
                        propertyCondition === "Recently Remodeled" ||
                        propertyCondition === "Well-Maintained – Traditional" ||
                        propertyCondition === "Well-Maintained – Contemporary"
                    ) {
                        newFilteredComps.push(element);
                    }
                };
                setFilteredComps(newFilteredComps);

                const newBestComps = [];
                for (let index = 0; index < amendedFullList.length; index++) {
                    const element = amendedFullList[index];
                    if ( newBestComps.length >= 3 ) {
                        break;
                    }
                    else if ( element.compDetails.overallScore < 0.5 ) {
                        continue;
                    }

                    const propertyCondition = element.compDetails.propertyCondition;
                    if ( 
                        propertyCondition === "Recently Remodeled" ||
                        propertyCondition === "Well-Maintained – Traditional" ||
                        propertyCondition === "Well-Maintained – Contemporary"
                    ) {
                        newBestComps.push(element);
                    }
                };

                setBestComps(newBestComps);
                setLoading(false);
            }
            else if ( getGateway.status === 405 ) {
                alert("Internal Server Error");
                await sendErrorEmail({
                    userId: savedUserId,
                    address: "",
                    notes: "Comps Results page",
                    propertyId: propertyId
                });
                navigate("/report-loading");
            }
            else {
                navigate("/");
            }
        };

        const querySavedProperty = async(newCompanyId, newProperty) => {
            const colRef = "companies";
            const docRef = newCompanyId;
            const subColRef = "properties";
            const subDocRef = newProperty.uniqueIds.propertyId;

            const queryData = await getSubDocument(colRef, docRef, subColRef, subDocRef);
            if ( queryData.status === 200 ) {
                const data = queryData.data.data;
                if ( data.selectedComps !== undefined ) {
                    setSelectedComps(data.selectedComps);
                }

                if ( data.propertyDescriptions !== undefined ) {
                    setQualifyingDescriptions(data.propertyDescriptions);
                }

                if ( data.conditionUpdate ) {
                    setConditionUpdate(data.conditionUpdate);
                }
            }
        };

        const handleWindowResize = () => {
			setInnerWidth(window.innerWidth);
		};
	  
		window.addEventListener('resize', handleWindowResize);

        if ( (propertyId === null || countyId === null) ) {
            navigate("/");
        }
        else if ( property === null ) {
            fetchUserData();
        }

    }, [navigate, countyId, property, propertyId, demoVersion]);

    const showScrollableImages = (item) => {
        setImageViewProperty(item);
        setViewScrollableImages(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
        recordEvent("view_scrollable_images", {});
    };

    const closeScrollableImages = () => {
        setViewScrollableImages(false);
        recordEvent("close_scrollable_images", {});
    };

    const openImageGallery = (index) => {
        setImageViewPropertyIndex(index);
        setViewImageGallery(true);
        setViewScrollableImages(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        recordEvent("open_image_gallery", {});
    };

    const closeImageGallery = () => {
        setViewImageGallery(false);
        setViewScrollableImages(true);
        setImageViewPropertyIndex(0);
        recordEvent("close_image_gallery", {});
    };

    const toggleSelectedComp = async(item) => {
        const cloneSelectedComps = [...selectedComps];
        const index = cloneSelectedComps.findIndex(e => e.zpid === item.uniqueIds.zillowId);
        if ( index === -1 ) {
            const zpid = item.uniqueIds.zillowId;
            const newObject = {
                zpid: zpid,
                type: null,
                date: new Date()
            }
            cloneSelectedComps.push(newObject);
            recordEvent("keep_comp", {
                zpid: item.uniqueIds.zillowId
            });
        }
        else {
            cloneSelectedComps.splice(index, 1);
            recordEvent("remove_comp", {
                zpid: item.uniqueIds.zillowId
            });
        }
        setSelectedComps(cloneSelectedComps);

        if ( companyId !== null ) {
            const colRef = "companies";
            const docRef = companyId;
            const subColRef = "properties";
            const subDocRef = property.uniqueIds.propertyId;
            setSelectedPropertyTransaction(colRef, docRef, subColRef, subDocRef, cloneSelectedComps);
            setSelectedPropertyReportTransaction("reports", docRef, subColRef, subDocRef, cloneSelectedComps);
        }

        if ( cloneSelectedComps.length === 0 ) {
            setExpanded(false);
        }
        else {
            const remainingCompsInSection = cloneSelectedComps.filter(e => e.type === selectedPanel).length;
            if ( remainingCompsInSection === 0 ) {
                setSelectedPanel("all");
            }
        }
    };

    const viewComp = (item) => {
        const id = item.uniqueIds.zillowId;
        setClickedZPID(id);

        const bestCompIndex = bestComps.findIndex(e => e.uniqueIds.zillowId === id);
        const descriptionIndex = qualifyingDescriptions.findIndex(e => e.zpid === id);
        if ( descriptionIndex === -1 && bestCompIndex === -1 ) {

        }

        if ( compsRef.current !== null ) {
            setTimeout(() => {
                compsRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }, 50);
        }
        recordEvent("comp_click", {
            zpid: id
        });
    };

    const openDetailedView = (item) => {
        setDetailedView(item);
        recordEvent("open_detailed_view", {
            zpid: item.uniqueIds.zillowId
        });
    };

    const closeDetailedView = () => {
        setDetailedView(null);
        recordEvent("close_detailed_view", {});
    };

    const onChangeZPIDHover = (id) => {
        setHoverZPID(id);
    };

    const shareLink = async() => {
        const colRef = "reports";
        const docRef = companyId;
        const subColRef = "properties";
        const subDocRef = property.uniqueIds.propertyId;

        // Generate the link you want to copy
        const linkToCopy = `${window.location.origin}/comps/${docRef}/${countyId}/${subDocRef}`;
        try {
            await navigator.clipboard.writeText(linkToCopy);
            setLinkCopied(true);
            setLinkCopiedError(false);
        } 
        catch (err) {
            console.log("Clipboard copy failed:", err);
            setLinkCopiedError(true);
        }

          const queryData = await getSubDocument(colRef, docRef, subColRef, subDocRef);
        if ( queryData.status !== 200 ) {
            await setSubData(colRef, docRef, subColRef, subDocRef, {selectedComps: selectedComps});
        }
        recordEvent("share_link", {});
    };

    const downloadProperties = async() => {
        setDownloadLoading(true);
        const url = process.env.REACT_APP_GENERATE_SPREADSHEET;
        
        const cloneSelectedComps = [...fullSelectedComps];
        for (let index = 0; index < cloneSelectedComps.length; index++) {
            const element = cloneSelectedComps[index];
            const selectedCompIndex = selectedComps.findIndex(e => e.zpid === element.uniqueIds.zillowId);
            if ( selectedCompIndex !== -1 ) {
                element.type = selectedComps[selectedCompIndex].type;
            }
            else {
                element.type = null;
            }    
        }

        const params = {
            userId: companyId,
            countyId: countyId,
            subjectProperty: property,
            comparables: cloneSelectedComps,
            downloadData: false,
            readOnly: false
        };

        const newWindow = window.open("", "_blank");
        const queryData = await cloudFunctionV2(url, params);
        if ( queryData.status === 200 ) {
            if ( newWindow === null ) {
                window.open(queryData.data, "_blank");
                newWindow.close();
            }
            else {
                newWindow.location.href = queryData.data;
            }
        }
        else {
            console.log("Error: ", queryData.error);
        }

        setDownloadLoading(false);
        recordEvent("download_properties", {});
    };
    
    const handleConditionSelect = (value) => {
        const cloneSelectedConditions = [...selectedConditions];
        const index = cloneSelectedConditions.indexOf(value);
        const allIndex = cloneSelectedConditions.indexOf("all");
        
        if ( value === "all" ) {
            cloneSelectedConditions.splice(0, cloneSelectedConditions.length);
            cloneSelectedConditions.push("all");
        }
        else if ( index === -1 ) {
            if ( allIndex !== -1 ) {
                cloneSelectedConditions.splice(allIndex, 1);
            }
            cloneSelectedConditions.push(value);
        }
        else {
            cloneSelectedConditions.splice(index, 1);
        }

        if ( cloneSelectedConditions.length === 0 ) {
            cloneSelectedConditions.push("all");
        }
        setSelectedConditions(cloneSelectedConditions);
        recordEvent("change_condition", {});

        applyFilters(selectedType, distance, recencyMonths, null, cloneSelectedConditions);
    };

    const closeDropdowns = () => {
        setCompTypeDropdownOpen(null);
    };

    const openSearch = () => {
        setSearchOpen(true);
        recordEvent("open_search", {});
    };

    const closeSearch = () => {
        setSearchOpen(false);
    };

    const changeAddress = (text) => {
        setAddress(text);

        const newFilteredComps = [];
        for (let index = 0; index < allComps.length; index++) {
            const element = allComps[index];
            const streetAddress = element.addressDetails.streetAddress.toLowerCase();
            const fullAddress = `${streetAddress}, ${element.addressDetails.city.toLowerCase()}, ${element.addressDetails.state.toLowerCase()} ${element.addressDetails.postalCode}`;
            const searchAddress = text.toLowerCase();
            if ( fullAddress.includes(searchAddress) ) {
                const newObject = {
                    label: `${element.addressDetails.streetAddress}, ${element.addressDetails.city}, ${element.addressDetails.state} ${element.addressDetails.postalCode}`,
                    value: element.uniqueIds.zillowId
                }
                newFilteredComps.push(newObject);
            }
            else if ( element.uniqueIds.zillowId.includes(text) ) {
                const newObject = {
                    label: `${element.addressDetails.streetAddress}, ${element.addressDetails.city}, ${element.addressDetails.state} ${element.addressDetails.postalCode}`,
                    value: element.uniqueIds.zillowId
                }
                newFilteredComps.push(newObject);
            }
        };
        
        setSuggestedComps(text === "" ? [] : newFilteredComps);
    };

    const clickSuggestedComp = (item) => {
        const compIndex = allComps.findIndex(e => e.uniqueIds.zillowId === item.value);
        viewComp(allComps[compIndex]);
        setSuggestedComps([]);
        setAddress("");
    };

    const closeFilterDropdowns = () => {
        setTypeDropdown(false);
        setHistogramDropdownOpen(false);
    };

    const applyFilters = async(type, miles, months, differences, newConditions) => {
        const newConditionFilteredComps = await filterByCondition(allComps, newConditions);
        const distanceFilteredComps = await filterByDistance(newConditionFilteredComps, miles);
        const recencyFilteredComps = await filterByRecency(distanceFilteredComps, months);
        const typeFilteredComps = await filterByType(recencyFilteredComps, type);
        const differenceFilteredComps = await filterBySimilarity(typeFilteredComps, differences, property);

        differenceFilteredComps.sort((a, b) => b.compDetails.overallScore - a.compDetails.overallScore);
        const newBestComps = differenceFilteredComps.slice(0, 3);
        setBestComps(newBestComps);
        setFilteredComps(differenceFilteredComps);
    };

    const reRunProperty = async() => {
        setLoadingReRun(true);
        const reRunParams = {
            propertyId: propertyId,
            userId: userId,
            countyId: countyId
        };
        const resourceId = "propertyDetailV2";
        const getGatewayQueryString = await propertyGatewayStringReRun("propertyDetailv2?", reRunParams);
        const getGatewayParams = {
            type: "gateway",
            resourceId: resourceId,
            queryString: getGatewayQueryString
        };
        const gatewayURL = process.env.REACT_APP_AWS_QUERY_URL;
        const queryData = await cloudFunctionV2(gatewayURL, getGatewayParams);
        if ( queryData.status === 200 ) {
            window.location.reload();
        }
        else {
            console.log("Error with force re-run: ", queryData);
            alert("Error with force re-run");
        }
        setLoadingReRun(false);
    };

    const toggleMap = () => {

    };

    const toggleCondition = () => {

    };

    const toggleMobileCompsList = () => {
        setCompsOverlayExpanded(!compsOverlayExpanded);
    };

    return (
        <div className={"run-comps-outer-container " + (typeDropdown === true || histogramDropdownOpen === true ? "relative-container" : "")}>
            <Header
                loggedIn={loggedIn}
            />
            {
                viewImageGallery === true && imageViewProperty !== null &&
                <ImageGallery
                    alt={imageViewProperty.addressDetails.streetAddress}
                    images={imageViewProperty.listingDetails.mlsPhotoUrls}
                    imageIndex={imageViewPropertyIndex}
                    setImageIndex={setImageViewPropertyIndex}
                    closeImageGallery={closeImageGallery}
                />
            }
            {
                viewScrollableImages === true && imageViewProperty !== null &&
                <ScrollableImages
                    images={imageViewProperty.listingDetails.mlsPhotoUrls}
                    closeScrollableImages={closeScrollableImages}
                    imageClick={openImageGallery}
                />
            }
            {
                selectedComps.length > 0 && loading === false &&
                <SelectedComps
                    allSelectedComps={selectedComps}
                    setAllSelectedComps={setSelectedComps}
                    fullSelectedComps={fullSelectedComps}
                    toggleSelectedComp={toggleSelectedComp}
                    expanded={expanded}
                    setExpanded={setExpanded}
                    showScrollableImages={showScrollableImages}
                    openDetailedView={openDetailedView}
                    downloadProperties={downloadProperties}
                    shareLink={shareLink}
                    downloadLoading={downloadLoading}
                    selectedPanel={selectedPanel}
                    setSelectedPanel={setSelectedPanel}
                    compTypeDropdownOpen={compTypeDropdownOpen}
                    setCompTypeDropdownOpen={setCompTypeDropdownOpen}
                    closeDropdowns={closeDropdowns}
                    companyId={companyId}
                    property={property}
                    linkCopied={linkCopied}
                    linkCopiedError={linkCopiedError}
                    readOnly={readOnly}
                    compTypeLegend={null}
                    isLoaded={isLoaded}
                    bestComps={bestComps}
                    innerWidth={innerWidth}
                    getARV={null}
                    arvLoading={false}
                    arvEstimate={null}
                    arvDescription={null}
                    getAsIs={null}
                    asIsEstimate={null}
                    asIsDescription={null}
                    onboardingStep={null}
                    nextOnboardingStep={null}
                    mobileSelectedCompsBody={mobileSelectedCompsBody}
                    autoKeepComps={null}
                    setArvDescriptionModal={null}
                    compsChanged={false}
                    setRegenerateARVErrorModal={null}
                    actionRequired={false}
                    setSmallChatExpanded={null}
                    bodyRef={bodyRef}
                />
            }
            {
                detailedView !== null &&
                <PropertyCardDetail
                    property={detailedView}
                    selected={selectedComps.findIndex(e => e.zpid === detailedView.uniqueIds.zillowId) !== -1 ? true : false}
                    closeDetailedView={closeDetailedView}
                    toggleSelectedComp={toggleSelectedComp}
                    showScrollableImages={showScrollableImages}
                    isLoaded={isLoaded}
                    subjectProperty={property}
                    clickedCompDescription={clickedCompDescription}
                    readOnly={readOnly}
                    bestComp={bestComps.findIndex(e => e.uniqueIds.zillowId === detailedView.uniqueIds.zillowId) !== -1 ? true : false}
                    loading={loading}
                    conditionUpdate={[]}
                />
            }
            {
                typeDropdown === true || histogramDropdownOpen === true ?
                <ExitScreen
                    clickScreen={() => closeFilterDropdowns()}
                />
                :
                null
            }
            {
                detailedView !== null &&
                <div className="dark-bg">
                </div>
            }
            {
                loadingReRun === true ?
                <ReportLoadingAnimation />
                :
                property === null ?
                <div className="run-comps-inner-container">
                </div>
                :
                <div className="run-comps-inner-container run-comps-test-container">
                    <div 
                        className="run-comps-body-container"
                        ref={bodyRef}
                    >
                        <div className="run-comps-header-container mobile-none">
                            <div className="run-comps-header-left-container">
                                <div className="run-comps-filters-container">
                                    {
                                        conditionOptions.map((item, index) =>
                                            <SelectionChip
                                                label={item.label}
                                                selected={item.selected}
                                                disabled={false}
                                                func={() => handleConditionSelect(item.value)}
                                                index={index}
                                                leftIcon={null}
                                                rightIcon={null}
                                                key={index}
                                            />
                                        )
                                    }
                                </div>
                                {
                                    filteredComps.length === allComps.length ?
                                    <span className="body-regular colour-primary">
                                        Showing <span className="body-bold">{filteredComps.length}</span> comps
                                    </span>
                                    :
                                    <span className="body-regular colour-primary">
                                        Showing <span className="body-bold">{filteredComps.length}</span> of {allComps.length} comps
                                    </span>
                                }
                            </div>
                            <div className="re-run-property-container">
                                <Button
                                    buttonType="primary-button"
                                    size="large"
                                    text={"Force re-run"}
                                    cta={reRunProperty}
                                    disabled={false}
                                    leftIcon={null}
                                    leftIconClass=""
                                    rightIcon={null}
                                    rightIconClass=""
                                    alt=""
                                    standardIcon={false}
                                    badge={false}
                                    badgeVal={null}
                                    badgeBG={null}
                                    buttonRef={null}
                                    spinner={false}
                                />
                            </div>
                            <div className="run-comps-header-right-container">
                                {
                                    searchOpen === true ?
                                    <div className="run-comps-header-search-input-container relative-container">
                                        <SearchInput
                                            value={address}
                                            onChange={(text) => changeAddress(text.target.value)}
                                            placeholder="Search"
                                            type="text"
                                            clearInput={() => changeAddress("")}
                                            magnifyingGlassFunc={() => closeSearch()}
                                            disabled={false}
                                        />
                                        {
                                            suggestedComps.length > 0 &&
                                            <PredictiveSearch
                                                loading={false}
                                                results={suggestedComps}
                                                selectItem={(item) => clickSuggestedComp(item)}
                                                nested={false}
                                            />
                                        }
                                    </div>
                                    :
                                    <div 
                                        className="text-button button-row gap-x-small"
                                        onClick={() => openSearch()}
                                    >
                                        <MagnifyingGlass
                                            fill={colour.gray05}
                                            className=""
                                            clickFunc={null}
                                        />
                                        <span className="body-regular colour-primary">
                                            Search
                                        </span>
                                    </div>
                                }
                            </div>
                        </div>
                        <div className="run-comps-mobile-header-container desktop-none">
                            <div className="run-comps-mobile-header-button-container">
                                <Button
                                    buttonType="secondary-button"
                                    size="large"
                                    text={"Map"}
                                    cta={toggleMap}
                                    disabled={false}
                                    leftIcon={<Settings01Icon size={16} color={colour.gray06} />}
                                    leftIconClass=""
                                    rightIcon={null}
                                    rightIconClass=""
                                    alt=""
                                    standardIcon={false}
                                    badge={false}
                                    badgeVal={null}
                                    badgeBG={null}
                                    buttonRef={null}
                                    spinner={false}
                                />
                            </div>
                            <div className="run-comps-mobile-header-button-container">
                                <Button
                                    buttonType="secondary-button"
                                    size="large"
                                    text={"Condition"}
                                    cta={toggleCondition}
                                    disabled={false}
                                    leftIcon={<YearBuilt fill={colour.gray07} className="" />}
                                    leftIconClass=""
                                    rightIcon={null}
                                    rightIconClass=""
                                    alt=""
                                    standardIcon={false}
                                    badge={false}
                                    badgeVal={null}
                                    badgeBG={null}
                                    buttonRef={null}
                                    spinner={false}
                                />
                            </div>
                        </div>
                        <CompFilters
                            selectedType={selectedType}
                            setSelectedType={setSelectedType}
                            typeDropdown={typeDropdown}
                            setTypeDropdown={setTypeDropdown}
                            distance={distance}
                            setDistance={setDistance}
                            recencyMonths={recencyMonths}
                            setRecencyMonths={setRecencyMonths}
                            selectedConditions={selectedConditions}
                            applyFilters={applyFilters}
                            bedsDifference={bedsDifference}
                            setBedsDifference={setBedsDifference}
                            bathsDifference={bathsDifference}
                            setBathsDifference={setBathsDifference}
                            sqftDifference={sqftDifference}
                            setSqftDifference={setSqftDifference}
                            yearBuiltDifference={yearBuiltDifference}
                            setYearBuiltDifference={setYearBuiltDifference}
                        />
                        <div className="run-comps-inner-body-container">
                            <div className="run-comps-map-container">
                                <AllCompsMap
                                    subjectProperty={property}
                                    comps={filteredComps}
                                    isMapLoaded={isLoaded}
                                    clickedZPID={clickedZPID}
                                    viewComp={viewComp}
                                    bestComps={bestComps}
                                    hoverZPID={hoverZPID}
                                    neighbourhoodData={neighbourhoodData}
                                    tracts={tracts}
                                    neighbourhoodBounds={neighbourhoodBounds}
                                    hoverMapLegend={hoverMapLegend}
                                    setHoverMapLegend={setHoverMapLegend}
                                    showHeatmap={showHeatmap}
                                    readOnly={readOnly}
                                    compTypeLegend={null}
                                    innerWidth={innerWidth}
                                    mapType={mapType}
                                    setMapType={setMapType}
                                    rawMap={rawMap}
                                    setRawMap={setRawMap}
                                    setSubjectPropertyHover={setSubjectPropertyHover}
                                    ceilings={[]}
                                    selectedComps={selectedComps}
                                />
                                {
                                    neighbourhoodData !== null &&
                                    <div className={"run-comp-map-bottom-container " + (showHeatmap === false ? "run-comp-hide-heatmap" : "")}>
                                        <MapLegend
                                            hoverMapLegend={hoverMapLegend}
                                            setHoverMapLegend={setHoverMapLegend}
                                            neighbourhoodOption={0}
                                        />
                                        <Switch
                                            label={"Heatmap"}
                                            val={showHeatmap}
                                            setVal={setShowHeatmap}
                                        />
                                    </div>
                                }
                                <div className="run-comps-map-button-container desktop-none">
                                    <Button
                                        buttonType="primary-button"
                                        size="large"
                                        text={compsOverlayExpanded === true ? "Map view" : "Comps list"}
                                        cta={toggleMobileCompsList}
                                        disabled={false}
                                        leftIcon={compsOverlayExpanded === true ? <Location fill={colour.gray00} className="" /> : <House className="" fill={colour.gray00} />}
                                        leftIconClass=""
                                        rightIcon={null}
                                        rightIconClass=""
                                        alt=""
                                        standardIcon={false}
                                        badge={false}
                                        badgeVal={null}
                                        badgeBG={null}
                                        buttonRef={null}
                                        spinner={false}
                                    />
                                </div>
                            </div>
                            <div className={"run-comps-subject-property-section mobile-none " + (subjectPropertyHover === true ? "run-comps-subject-property-section-hover" : "")}>
                                <div className="run-comps-subject-property-title-container">
                                    <div className="relative-container">
                                        <MapBubble
                                            className=""
                                            fill={colour.gray07}
                                            stroke={colour.gray00}
                                            strokeWidth="0.5"
                                        />
                                        <House
                                            fill={colour.gray00}
                                            className="all-comps-map-house-icon"
                                        />
                                    </div>
                                    <span className="body-bold colour-primary">
                                        Subject property
                                    </span>
                                </div>
                                <PropertyCard
                                    property={property}
                                    subjectProperty={true}
                                    subjectPropertyObject={property}
                                    showScrollableImages={showScrollableImages}
                                    detail={false}
                                    isLoaded={isLoaded}
                                    testView={true}
                                    alwaysActive={true}
                                    selected={false}
                                    toggleSelectedComp={null}
                                    bestComp={false}
                                    openDetailedView={null}
                                    clickedZPID={clickedZPID}
                                    clickedCompDescription={null}
                                    loading={false}
                                    onChangeZPIDHover={null}
                                    readOnly={readOnly}
                                    innerWidth={innerWidth}
                                    subjectPropertyHover={subjectPropertyHover}
                                    gptLoading={false}
                                    priceCeiling={false}
                                    priceCeilingText={null}
                                    darkMode={false}
                                    conditionUpdate={[]}
                                />
                            </div>
                            <div 
                                className="run-comps-cards-section"
                                ref={compsRef}
                            >
                                {
                                    clickedZPID !== null &&
                                    <PropertyCard
                                        property={clickedComp}
                                        subjectProperty={false}
                                        subjectPropertyObject={property}
                                        showScrollableImages={showScrollableImages}
                                        isLoaded={isLoaded}
                                        testView={true}
                                        alwaysActive={true}
                                        selected={selectedComps.findIndex(e => e.zpid === clickedComp.uniqueIds.zillowId) !== -1 ? true : false}
                                        toggleSelectedComp={() => toggleSelectedComp(clickedComp)}
                                        bestComp={bestComps.findIndex(e => e.uniqueIds.zillowId === clickedComp.uniqueIds.zillowId) !== -1 ? true : false}
                                        openDetailedView={openDetailedView}
                                        clickedZPID={clickedZPID}
                                        clickedCompDescription={clickedCompDescription}
                                        loading={loading}
                                        onChangeZPIDHover={null}
                                        readOnly={readOnly}
                                        innerWidth={innerWidth}
                                        subjectPropertyHover={false}
                                        gptLoading={false}
                                        priceCeiling={false}
                                        priceCeilingText={null}
                                        darkMode={false}
                                        conditionUpdate={conditionUpdate}
                                    />
                                }
                                {
                                    bestComps.length > 0 &&
                                    <div className="run-comps-best-comps-title-container">
                                        <div className="relative-container">
                                            <MapBubble
                                                className=""
                                                fill={colour.jade01}
                                                stroke={colour.jade02}
                                                strokeWidth="0.5"
                                            />
                                            <House
                                                fill={colour.jade03}
                                                className="all-comps-map-house-icon"
                                            />
                                        </div>
                                        <span className="body-bold colour-primary">
                                            Suggested comps
                                        </span>
                                    </div>
                                }
                                {
                                    bestComps.map((item, index) =>
                                        item.uniqueIds.zillowId !== clickedZPID &&
                                        <PropertyCard
                                            property={item}
                                            subjectProperty={false}
                                            subjectPropertyObject={property}
                                            showScrollableImages={showScrollableImages}
                                            isLoaded={isLoaded}
                                            key={index}
                                            testView={true}
                                            alwaysActive={true}
                                            selected={selectedComps.findIndex(e => e.zpid === item.uniqueIds.zillowId) !== -1 ? true : false}
                                            toggleSelectedComp={() => toggleSelectedComp(item)}
                                            bestComp={true}
                                            openDetailedView={openDetailedView}
                                            clickedZPID={clickedZPID}
                                            clickedCompDescription={null}
                                            loading={false}
                                            onChangeZPIDHover={onChangeZPIDHover}
                                            readOnly={readOnly}
                                            innerWidth={innerWidth}
                                            subjectPropertyHover={false}
                                            gptLoading={false}
                                            priceCeiling={false}
                                            priceCeilingText={null}
                                            darkMode={false}
                                            conditionUpdate={conditionUpdate}
                                        />
                                    )
                                }
                            </div>
                        </div>
                        <CompsOverlay
                            expanded={compsOverlayExpanded}
                            property={property}
                            showScrollableImages={showScrollableImages}
                            isLoaded={isLoaded}
                            clickedZPID={clickedZPID}
                            readOnly={readOnly}
                            compsRef={compsRef}
                            clickedComp={clickedComp}
                            bestComps={bestComps}
                            selectedComps={selectedComps}
                            loading={loading}
                            clickedCompDescription={clickedCompDescription}
                            toggleSelectedComp={toggleSelectedComp}
                            openDetailedView={openDetailedView}
                            onChangeZPIDHover={onChangeZPIDHover}
                            innerWidth={innerWidth}
                            filteredComps={filteredComps}
                            allComps={allComps}
                            gptLoading={false}
                            ceilings={[]}
                            conditionUpdate={conditionUpdate}
                        />
                    </div>
                    {/* <Histogram
                        filteredComps={filteredComps}
                        histogramDropdownOpen={histogramDropdownOpen}
                        setHistogramDropdownOpen={setHistogramDropdownOpen}
                    /> */}
                </div>
            }
        </div>
    )
};

export default TestView;